import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { staffingRequestKeys } from "@/lib/query";

// ── Types ──────────────────────────────────────────────────────────────────

export type RequestStatus =
  | "New"
  | "Mapping"
  | "Mapped"
  | "Committed"
  | "Rejected"
  | "Superseded"
  | "Error";
export type SourceType = "SOW" | "ResourcePlan" | "ExternalHook" | "Template";
export type LineStatus = "Unmapped" | "Mapped" | "Committed" | "Rejected";
export type MappedRole = "R1" | "R2" | "R3" | "R4" | "Supporting";
export type PodComposition = "Solo" | "Pair" | "Trio" | "Quad";
export type EventType =
  | "ingested"
  | "linked_solution"
  | "line_mapped"
  | "line_resolved"
  | "intensity_confirmed"
  | "pod_staged"
  | "committed"
  | "partially_committed"
  | "rejected"
  | "parse_error"
  | "title_renamed";

export interface StaffingRequestRow {
  id: string;
  status: RequestStatus;
  sourceType: SourceType;
  sourceChannel: string;
  /** NULL for unparseable (Error) uploads — fall back to originalFilename. */
  title: string | null;
  originalFilename: string | null;
  clientAccountId: string | null;
  accountName: string | null;
  solutionId: string | null;
  solutionName: string | null;
  linkedSolution: { id: string; name: string } | null;
  isUnlinked: boolean;
  suggestedIntensity: number | null;
  createdAt: string;
  lineCount: number;
  unmappedCount: number;
}

export interface StaffingRequestDetail {
  id: string;
  status: RequestStatus;
  sourceType: SourceType;
  /** NULL for unparseable (Error) uploads — fall back to originalFilename. */
  title: string | null;
  suggestedIntensity: number | null;
  confirmedIntensity: number | null;
  /** Template Section A — the submitter's free-text names, verbatim. */
  sourceAccountName: string | null;
  sourceSolutionName: string | null;
  /** Template Section C — month the staffing (and suggested Intensity) begins. */
  sourceStartMonth: string | null;
  originalFilename: string | null;
  parseError: string | null;
  createdAt: string;
  committedAt: string | null;
}

/** Template Section B pick stored as a suggestion (never written to truth tables). */
export interface ConstraintSuggestion {
  id: string;
  groupName: "Offering" | "Platform" | "Domain" | "ICP" | string;
  value: string;
  constraintId: string | null;
  /** Live Configuration name the value resolved to at ingest; null = unrecognized. */
  resolvedName: string | null;
}

export interface RequestLine {
  id: string;
  rowIndex: number;
  sourceRoleLabel: string;
  sourceResourceName: string | null;
  /** Short summary label (e.g. "Jul–Sep 2026") — fallback display when no periodColumns. */
  sourcePeriod: string | null;
  /** Per-period hours breakdown in source column order. */
  sourcePeriods: Array<{ period: string | null; hours: number }>;
  /** Total source hours across all periods. Context only — never converted to points. */
  sourceHours: number;
  suggestedDisciplineId: string | null;
  suggestedRole: MappedRole | null;
  mappedDisciplineId: string | null;
  mappedRole: MappedRole | null;
  mappedResourceId: string | null;
  /** Display name of the mapped Resource (resolved server-side); null when Open. */
  mappedResourceName: string | null;
  lineStatus: LineStatus;
}

export interface RequestEvent {
  id: string;
  eventType: EventType;
  suggestedValue: string | null;
  humanValue: string | null;
  reason: string | null;
  eventAt: string;
  lineId: string | null;
}

export interface RequestAccount {
  id: string;
  name: string;
  /** The Account's CURRENT Expectation Level — inherited, read-only on the request. */
  expectationLevel: number;
}

/** One entry of the matched Account's constraint profile (the §7 defaults). */
export interface AccountConstraintEntry {
  id: string;
  groupName: string;
  name: string;
}

export interface RequestLinkedSolution {
  id: string;
  name: string;
}

/** One active member of the linked Solution's current team (Workflow 2 context). */
export interface CurrentPodMember {
  role: MappedRole;
  resourceId: string;
  resourceName: string;
}

export interface StaffingRequestDetailResponse {
  request: StaffingRequestDetail;
  lines: RequestLine[];
  events: RequestEvent[];
  account: RequestAccount | null;
  /** Matched Account's constraint profile — the defaults for a known Account (§7). */
  accountConstraints: AccountConstraintEntry[];
  linkedSolution: RequestLinkedSolution | null;
  /** The linked Solution's ACTIVE team — empty when unlinked or unstaffed. */
  currentPod: CurrentPodMember[];
  /** Union of period labels from all lines, in first-seen order. Used for horizontal hours columns. */
  periodColumns: string[];
  /** Template Section B constraint picks (empty for non-template sources). */
  constraintSuggestions: ConstraintSuggestion[];
}

// ── Commit Preview types ───────────────────────────────────────────────────

export interface PodPositionPreview {
  role: string;
  resourceId: string | null;
  resourceName: string | null;
  points: number;
  disciplineCode: string | null;
}

export interface PodChangeItem {
  role: string;
  type: "add" | "replace" | "unchanged" | "remove";
  fromResourceName: string | null;
  toResourceName: string | null;
}

export interface CommitMismatch {
  lineId: string;
  role: "R1" | "R2" | "R3" | "R4";
  resourceName: string | null;
  expectedDisciplineCode: string | null;
  assignmentDisciplineCode: string | null;
}

export interface CommitPreview {
  solutionId: string;
  solutionName: string;
  currentIntensity: number;
  suggestedIntensity: number | null;
  /**
   * The Pod size the preview is computed for — defaults server-side to the size
   * suggested from the mapped Primary positions (RM override wins).
   */
  podSize: PodComposition;
  /** Size suggested from the mapped Primary lines; null when none are mapped. */
  suggestedPodSize: PodComposition | null;
  /** In-range Primary positions that will be staged Open (no Resource). Never blocks. */
  unresolvedCount: number;
  current: PodPositionPreview[];
  proposed: PodPositionPreview[];
  changes: PodChangeItem[];
  /** Mapped Primary lines beyond the chosen size — block commit until resolved. */
  overflow: Array<{ role: string; sourceRoleLabel: string }>;
  /** Slots mapped by more than one line — block commit until resolved. */
  duplicates: Array<{ role: string; count: number }>;
  /** Currently-assigned Resources that shrinking to the chosen size would release. */
  releasedCount: number;
  /** Named-Resource Primary lines that will require a per-line mismatch acknowledgement. */
  mismatches: CommitMismatch[];
}

export interface IntensitySuggestion {
  intensity: number;
  basisHours: number;
  basisPeriod: string;
}

/** One row of the Discipline-scoped Resource search (mapping workspace picker). */
export interface DisciplineResource {
  id: string;
  name: string;
}

// ── Fetch helper ──────────────────────────────────────────────────────────

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ── Query hooks ───────────────────────────────────────────────────────────

export type QueueLinked = "linked" | "unlinked";
export type QueueDirection = "next" | "prev";

export interface StaffingRequestQuery {
  statuses?: string[];
  linked?: QueueLinked;
  cursor?: string;
  direction?: QueueDirection;
  limit?: number;
}

export interface StaffingRequestPage {
  data: StaffingRequestRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export function useStaffingRequests(query: StaffingRequestQuery = {}) {
  const search = new URLSearchParams();
  if (query.statuses && query.statuses.length > 0) search.set("status", query.statuses.join(","));
  if (query.linked) search.set("linked", query.linked);
  if (query.cursor) search.set("cursor", query.cursor);
  if (query.direction) search.set("direction", query.direction);
  if (query.limit) search.set("limit", String(query.limit));
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return useQuery({
    queryKey: staffingRequestKeys.list(query),
    queryFn: () => jsonFetch<StaffingRequestPage>(`/api/staffing-requests${suffix}`),
    // Keep the prior page visible while the next/prev page loads — no flicker.
    placeholderData: keepPreviousData,
  });
}

export function useStaffingRequestDetail(id: string) {
  return useQuery({
    queryKey: staffingRequestKeys.detail(id),
    queryFn: () => jsonFetch<StaffingRequestDetailResponse>(`/api/staffing-requests/${id}`),
  });
}

export function useIntensitySuggestion(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...staffingRequestKeys.detail(id), "intensity-suggestion"],
    queryFn: () =>
      jsonFetch<{ suggestion: IntensitySuggestion | null }>(
        `/api/staffing-requests/${id}/suggest-intensity`,
      ).then((r) => r.suggestion),
    enabled,
    staleTime: 60_000,
  });
}

export function useCommitPreview(requestId: string, enabled: boolean, podSize?: PodComposition) {
  return useQuery({
    queryKey: [...staffingRequestKeys.detail(requestId), "commit-preview", podSize ?? "current"],
    queryFn: () => {
      const suffix = podSize ? `?podSize=${podSize}` : "";
      return jsonFetch<CommitPreview>(
        `/api/staffing-requests/${requestId}/commit-preview${suffix}`,
      );
    },
    enabled,
    staleTime: 0,
  });
}

/**
 * Discipline-scoped Resource search for the mapping workspace. Disabled until a
 * Discipline is chosen (v2 rule: no Resource pick without a Discipline).
 */
export function useDisciplineResources(disciplineId: string | null, search: string) {
  return useQuery({
    queryKey: [...staffingRequestKeys.all, "discipline-resources", disciplineId, search],
    queryFn: () => {
      const params = new URLSearchParams({ disciplineId: disciplineId ?? "" });
      if (search.trim()) params.set("search", search.trim());
      return jsonFetch<{ resources: DisciplineResource[] }>(
        `/api/staffing-requests/resources?${params.toString()}`,
      ).then((r) => r.resources);
    },
    enabled: disciplineId !== null,
    staleTime: 30_000,
  });
}

// ── Mutation hooks ────────────────────────────────────────────────────────

export function useUploadStaffingRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { file: File; sourceType: SourceType }) => {
      const form = new FormData();
      form.append("file", payload.file);
      form.append("sourceType", payload.sourceType);
      return jsonFetch<{ request: StaffingRequestRow; deduped: boolean }>(
        "/api/staffing-requests",
        { method: "POST", body: form },
      );
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      if (result.deduped) {
        toast.success("Request already exists — existing record shown.");
      } else {
        toast.success("Staffing request uploaded successfully.");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * Re-parse a failed (Error) request's retained artifact through the current parser.
 * On a successful re-parse the server supersedes the Error attempt and returns the
 * recovered head; a still-unparseable artifact returns the unchanged Error request.
 */
export function useRetryStaffingRequest(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<{ request: { id: string; status: RequestStatus; parseError: string | null } }>(
        `/api/staffing-requests/${requestId}/retry`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      if (result.request.status === "Error") {
        toast.error(`Still couldn't parse: ${result.request.parseError ?? "unknown error"}`);
      } else {
        toast.success("Parsed successfully — request recovered.");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useLinkSolution(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (solutionId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/staffing-requests/${requestId}/link-solution`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ solutionId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.detail(requestId) });
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Solution linked.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateRequestTitle(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (title: string) =>
      jsonFetch<{ id: string; title: string }>(`/api/staffing-requests/${requestId}/title`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.detail(requestId) });
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Request renamed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function usePatchLine(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      lineId: string;
      // null clears the mapping; undefined leaves it unchanged (server semantics).
      mappedDisciplineId?: string | null;
      mappedRole?: MappedRole | null;
      mappedResourceId?: string | null;
    }) => {
      const { lineId, ...body } = payload;
      return jsonFetch<RequestLine>(`/api/staffing-requests/${requestId}/lines/${lineId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.detail(requestId) });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCommitRequest(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      podSize: PodComposition;
      confirmedIntensity: number;
      mismatchAcknowledgementReasons?: Record<string, string>;
    }) =>
      jsonFetch<{ committedLineIds: string[]; skippedLineIds: string[]; status: RequestStatus }>(
        `/api/staffing-requests/${requestId}/commit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.detail(requestId) });
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      const skipped = result.skippedLineIds.length;
      if (skipped > 0) {
        toast.success(
          `Committed ${result.committedLineIds.length} line(s). ${skipped} line(s) skipped (unmapped).`,
        );
      } else {
        toast.success(`All ${result.committedLineIds.length} line(s) committed.`);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useRejectRequest(requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) =>
      jsonFetch<{ ok: boolean }>(`/api/staffing-requests/${requestId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: staffingRequestKeys.detail(requestId) });
      qc.invalidateQueries({ queryKey: staffingRequestKeys.all });
      toast.success("Request rejected.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
